import { fitStaticMapCamera, quantizeCenterToPixelGrid } from '@/src/lib/map/fitMapCamera';

type StaticMapOptions = {
  lat: number;
  lon: number;
  width: number;
  height: number;
  zoom: number;
  scale?: number;
  format?: 'png' | 'jpg';
};

export type StaticMapBounds = {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
};

// Largest `size` the Maps Static API accepts for our keys -- `scale` multiplies the
// pixels it returns, not this logical size. Over it Google clamps without moving the
// zoom, so the image frames less ground than the JS map (HP-9082).
export const MAX_STATIC_MAP_LOGICAL_DIMENSION = 2048;

// Our own ceiling on what the unauthenticated proxy will bill to Google. Both the
// builder and the route's validator apply it, or the builder emits URLs the validator
// rejects -- it bounds `size * scale`, which the logical cap alone does not.
export const MAX_STATIC_MAP_OUTPUT_DIMENSION = 2048;

// Cloud-styled "Static map" in the hotpads Google project (edit in Map
// Management); the env override covers environments whose signing key lacks it.
const HOTPADS_STATIC_MAP_ID = process.env.NEXT_PUBLIC_GOOGLE_MAPS_STATIC_MAP_ID ?? '83226b537847112eb3c8eb77';
/**
 * Cache buster for the derived image, not just the camera: the proxy URL carries only
 * bounds and panel size, while zoom, size and scale are derived server-side, and
 * responses live a day in the browser and a week in the CDN. Bump on every change to
 * the rendering, including a second one in the same branch.
 */
export const STATIC_MAP_CAMERA_VERSION = '8';

// Sub-pixel at the API's max zoom of 21 even at scale 2, so the printed form
// always round-trips the quantized grid point.
const CENTER_DECIMALS = 7;

function formatCenterComponent(value: number): string {
  if (!Number.isFinite(value)) return String(value);
  return value.toFixed(CENTER_DECIMALS).replace(/\.?0+$/, '');
}

export function buildStaticMapUrl({ lat, lon, width, height, zoom, scale = 2, format }: StaticMapOptions): string {
  const center = quantizeCenterToPixelGrid({ lat, lng: lon }, zoom, scale);

  let url = 'https://maps.googleapis.com/maps/api/staticmap?';
  url += `size=${width}x${height}&`;
  url += `zoom=${zoom}&`;
  url += `scale=${scale}&`;
  if (format) {
    url += `format=${format}&`;
  }
  url += `center=${formatCenterComponent(center.lat)},${formatCenterComponent(center.lng)}&`;

  url += `map_id=${HOTPADS_STATIC_MAP_ID}&`;

  // Remove trailing &
  if (url.endsWith('&')) {
    url = url.slice(0, -1);
  }

  return url;
}

/**
 * Unsigned Google Static Maps URL centered on the given bounds. Pair with
 * buildStaticMapProxyUrl so the /api/static-map route signs and fetches it.
 */
export function buildStaticMapUrlForBounds(
  bounds: StaticMapBounds,
  { width, height, scale, format }: { width: number; height: number; scale?: number; format?: StaticMapOptions['format'] },
): string {
  const camera = fitStaticMapCamera(
    {
      northLat: bounds.maxLat,
      southLat: bounds.minLat,
      eastLng: bounds.maxLon,
      westLng: bounds.minLon,
    },
    { width, height },
    MAX_STATIC_MAP_LOGICAL_DIMENSION,
  );
  if (!camera) return '';

  return buildStaticMapUrl({
    lat: camera.center.lat,
    lon: camera.center.lng,
    width: camera.width,
    height: camera.height,
    zoom: camera.zoom,
    // Cover the panel where Google's max scale of 2 allows it -- beyond one halving it
    // cannot, and the image under-covers. Clamped by the output ceiling either way.
    scale: Math.min(
      scale ?? Math.min(2, 2 ** camera.halvings),
      Math.max(1, Math.floor(MAX_STATIC_MAP_OUTPUT_DIMENSION / Math.max(camera.width, camera.height))),
    ),
    format,
  });
}

export function buildStaticMapUrlForViewport(
  bounds: StaticMapBounds,
  viewport: { width: number; height: number },
): string {
  return buildStaticMapUrlForBounds(bounds, { width: viewport.width, height: viewport.height, format: 'jpg' });
}

export function buildStaticMapProxyUrl(unsignedUrl: string): string {
  return `/api/static-map?url=${encodeURIComponent(unsignedUrl)}`;
}
